/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { Column } from '@g17eco/molecules/tracking-list';
import { ScopeQuestionGroupOptionalValue } from '../../../../types/survey';
import { BatchResult } from '../../../../api/ai-supervisor';
import { QuestionState } from '../../hooks/useBatchSSE';
import { Progress } from 'reactstrap';

export const GroupColumnProgress = (props: {
  questionGroup: ScopeQuestionGroupOptionalValue;
  getLiveState: (utrvId: string) => QuestionState | undefined;
  getResult: (utrvId: string) => BatchResult | undefined;
}) => {
  const { questionGroup, getLiveState, getResult } = props;

  // Calculate per-group stats based on questions in this group
  const groupStats = questionGroup.list.reduce(
    (acc, question) => {
      const utrvId = question.utrv?._id;
      if (!utrvId) return acc;
      const liveState = getLiveState(utrvId);
      const result = getResult(utrvId);

      if (liveState?.status === 'completed' || result?.status === 'completed') {
        acc.completed++;
      } else if (liveState?.status === 'processing') {
        acc.processing++;
      } else if (liveState?.status === 'failed' || result?.status === 'failed') {
        acc.failed++;
      } else {
        acc.pending++;
      }
      return acc;
    },
    { pending: 0, completed: 0, processing: 0, failed: 0 }
  );

  const total = questionGroup.list.length;
  const progress = total > 0 ? Math.round((groupStats.completed / total) * 100) : 0;

  return (
    <Column>
      <div className='text-lg d-flex align-items-center gap-4 me-3'>
        <div className='d-flex align-items-center gap-2 small'>
          <i className='fal fa-clock text-ThemeTextMedium' />
          <span className='text-ThemeTextDark'>{groupStats.pending}</span>
        </div>
        <div className='d-flex align-items-center gap-2 small'>
          <i
            className={`fal  text-ThemeAccentMedium ${groupStats.processing > 0 ? 'fa-spinner fa-spin' : 'fa-hourglass-clock'}`}
          />
          <span className='text-ThemeTextDark'>{groupStats.processing}</span>
        </div>
        <div className='d-flex align-items-center gap-2 small'>
          <i className='fal fa-check-circle text-ThemeSuccessMedium' />
          <span className='text-ThemeTextDark'>{groupStats.completed}</span>
        </div>
        <div className='d-flex align-items-center gap-2 small'>
          <i className='fal fa-exclamation-triangle text-ThemeDangerMedium' />
          <span className='text-ThemeTextDark'>{groupStats.failed}</span>
        </div>
        <div style={{ width: '8rem' }}>
          <Progress
            value={progress}
            style={{ height: '0.5rem' }}
          />
        </div>
      </div>
    </Column>
  );
};
